// ── Throttle API service ────────────────────────────────────────────────────
// Read-only client for the PlayCDU Throttle API. The API is public (no auth)
// and CORS-open, so it is called directly from the browser.
//
// Notable behaviours encoded here:
// - /v1/health returns a meaningful JSON body on BOTH 200 (fresh) and 503
//   (stale/failing) — a 503 is a data-freshness signal, not a request failure.
// - Collection routes return 404 before the first successful poll (cold start)
//   and /v1/servers/:fqdn returns 404 for unknown servers. Both are surfaced
//   as `null` so callers can render dedicated empty states.
// - Full-dump routes return 404 once a dump expires (24 h after it was taken);
//   the metadata list can be up to a minute ahead of reality.
// - Envelope schema version is checked once per session and mismatches are
//   logged loudly (a `v` bump means response shapes changed).

import type {
  AntiLagData,
  CacheEnvelope,
  DumpsIndex,
  PerformanceDump,
  ServerDumpsResponse,
  ThrottleClusterResponse,
  ThrottleHealth,
  ThrottleServerDetail,
  ThrottleServerIndex,
} from '../types/throttle';
import { THROTTLE_SCHEMA_VERSION } from '../types/throttle';

const API_BASE_URL = 'https://api.playcdu.co/throttle/v1';

/** In-memory config cache — the document only changes when staff edit it. */
const CONFIG_CACHE_TTL = 10 * 60 * 1000;

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    // Not JSON — fall through to status text.
  }
  return response.statusText || `HTTP ${response.status}`;
}

let schemaWarningLogged = false;

/** Warns once per session when the API starts serving a different schema version. */
function checkSchemaVersion(value: unknown): void {
  if (schemaWarningLogged || value == null || typeof value !== 'object') return;
  const v = (value as { v?: unknown }).v;
  if (typeof v === 'number' && v !== THROTTLE_SCHEMA_VERSION) {
    schemaWarningLogged = true;
    console.warn(
      `[throttleApi] Unexpected schema version ${v} (expected ${THROTTLE_SCHEMA_VERSION}) — ` +
        'response shapes may have changed; the Chunk Throttle views may render incorrectly.',
    );
  }
}

class ThrottleApiService {
  private configCache: CacheEnvelope<AntiLagData> | null = null;
  private configCachedAt = 0;
  /** Dumps are immutable once cached — fetched tables are kept for the session, keyed `fqdn:n`. */
  private dumpCache = new Map<string, CacheEnvelope<PerformanceDump>>();

  /**
   * Shared GET helper. Returns `null` on 404 (cold start / unknown server /
   * expired dump), throws on any other non-OK status or network failure.
   */
  private async request<T>(endpoint: string): Promise<T | null> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`);
    if (response.status === 404) return null;
    if (!response.ok) {
      const message = await extractErrorMessage(response);
      throw new Error(`Throttle API Error: ${response.status} - ${message}`);
    }
    const body = (await response.json()) as T;
    checkSchemaVersion(body);
    return body;
  }

  /**
   * Poll-freshness health check. Parses the body on both 200 and 503 —
   * a 503 simply means the pipeline is stale/failing and is reported via
   * `state`. Requesting this route during a cold start also kicks off the
   * API's one-time bootstrap poll.
   */
  async getHealth(): Promise<ThrottleHealth> {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (response.ok || response.status === 503) {
      return response.json();
    }
    const message = await extractErrorMessage(response);
    throw new Error(`Throttle API Error: ${response.status} - ${message}`);
  }

  /**
   * Whole-fleet summary index. Returns `null` before the first successful
   * backend poll (cold start).
   */
  async getServers(): Promise<CacheEnvelope<ThrottleServerIndex> | null> {
    return this.request<CacheEnvelope<ThrottleServerIndex>>('/servers');
  }

  /**
   * Merged single-server view (status + perf + throttling + dumps; `status`
   * and the slices are independently nullable). Returns `null` when the
   * server is unknown.
   */
  async getServer(fqdn: string): Promise<ThrottleServerDetail | null> {
    return this.request<ThrottleServerDetail>(`/servers/${encodeURIComponent(fqdn)}`);
  }

  /**
   * Every server sharing a Quasar cluster id (segment must not contain dots).
   * Returns `null` when no servers match. Currently unused by the views —
   * the overview groups the index client-side — but kept for completeness.
   */
  async getCluster(clusterId: string): Promise<ThrottleClusterResponse | null> {
    return this.request<ThrottleClusterResponse>(`/servers/${encodeURIComponent(clusterId)}`);
  }

  /**
   * Dump metadata for one server (no chunk tables). Returns `null` when the
   * server is unknown; an empty `entries` list is the normal state.
   */
  async getServerDumps(fqdn: string): Promise<ServerDumpsResponse | null> {
    return this.request<ServerDumpsResponse>(`/servers/${encodeURIComponent(fqdn)}/dumps`);
  }

  /**
   * One full performance dump (every sampled chunk, slowest first). Returns
   * `null` on 404 — the dump expired (24 h) or never existed. Content never
   * changes once cached, so successful fetches are cached for the session.
   */
  async getDump(fqdn: string, n: number): Promise<CacheEnvelope<PerformanceDump> | null> {
    const key = `${fqdn}:${n}`;
    const cached = this.dumpCache.get(key);
    if (cached) return cached;
    const dump = await this.request<CacheEnvelope<PerformanceDump>>(
      `/servers/${encodeURIComponent(fqdn)}/dumps/${n}`,
    );
    if (dump) this.dumpCache.set(key, dump);
    return dump;
  }

  /**
   * Fleet-wide dump metadata ("recent investigations"). Returns `null` before
   * the first successful backend poll (cold start).
   */
  async getDumpsIndex(): Promise<CacheEnvelope<DumpsIndex> | null> {
    return this.request<CacheEnvelope<DumpsIndex>>('/dumps');
  }

  /**
   * Network-wide anti-lag configuration. Returns `null` before config has
   * been loaded (cold start). Cached in-memory for 10 minutes.
   */
  async getConfig(forceRefresh = false): Promise<CacheEnvelope<AntiLagData> | null> {
    const now = Date.now();
    if (!forceRefresh && this.configCache && now - this.configCachedAt < CONFIG_CACHE_TTL) {
      return this.configCache;
    }
    const config = await this.request<CacheEnvelope<AntiLagData>>('/config');
    if (config) {
      this.configCache = config;
      this.configCachedAt = now;
    }
    return config;
  }
}

export const throttleApiService = new ThrottleApiService();
